import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import ReactHighcharts from 'react-highcharts';
import { injectIntl } from 'react-intl';
import { grey500 } from 'material-ui/styles/colors';
import {
  tntBlue,
  tntGreen,
  tntRed,
  tntOrange,
  tntPink,
  tntPurple,
  tntYellow,
  tntTeal,
} from '../../theme/tntColors';
import moment from 'moment';
import _ from 'lodash';

/**
 * getCollapseTimePeriodFunction
 * @param {*} timePeriod 
 * @returns 
 */
function getCollapseTimePeriodFunction(timePeriod) {
  return (arr, { date, csat, count }) => {
    const lastItemTimeMs = arr[arr.length - 1] && arr[arr.length - 1].x;
    const thisItemTimeMs = moment.utc(date).startOf(timePeriod).valueOf();
    if (lastItemTimeMs === thisItemTimeMs) {
      const lastCount = arr[arr.length - 1].count;
      const lastCsat = arr[arr.length - 1].y;
      arr[arr.length - 1].y = ((lastCount * lastCsat) + (count * csat)) / (lastCount + count);
      arr[arr.length - 1].count += count;
      return arr;
    } else {
      return arr.concat([{
        x: moment.utc(date).startOf(timePeriod).valueOf(),
        y: csat * 100,
        count: count,
      }]);
    }
  };
}

/**
 * getData
 * @param {*} csatByDate 
 * @param {*} collapseTimePeriodTo 
 * @returns 
 */
function getData(csatByDate, collapseTimePeriodTo) {
  if (!Array.isArray(csatByDate) || csatByDate.length <= 0) {
    return [];
  } else if (!collapseTimePeriodTo) {
    return csatByDate.map(({ date, csat, count }) => {
      return {
        x: Date.parse(date),
        y: csat * 100,
        count: count,
      };
    });
  } else {
    return csatByDate.reduce(getCollapseTimePeriodFunction(collapseTimePeriodTo), []);
  }
}

function getSeriesColor(index) {
  const colors = [
    tntBlue,
    tntOrange,
    tntPurple,
    tntYellow,
    tntPink,
    tntTeal,
    tntGreen,
    tntRed,
    grey500,
  ];
  return colors[index % colors.length];
}

function getSeries(csatByDeviceAndDate, intl) {
  if (!Array.isArray(csatByDeviceAndDate) || csatByDeviceAndDate.length <= 0) {
    return {
      series: [],
      tooltipDateFormatter: () => null,
      xAxisDateFormatter: () => null,
    };
  }

  const { firstDate, lastDate } = csatByDeviceAndDate
    .map(x => {
      if (Array.isArray(x.csatByDate) && x.csatByDate.length > 0) {
        return {
          firstDate: x.csatByDate[0].date,
          lastDate: x.csatByDate[x.csatByDate.length - 1].date,
        };
      } else {
        return null;
      }
    })
    .filter(x => !_.isNil(x))
    .reduce((obj, { firstDate, lastDate }) => {
      if (!obj.firstDate || !obj.lastDate) {
        return { firstDate, lastDate };
      } else {
        return {
          firstDate: moment(obj.firstDate).diff(moment(firstDate)) > 0 ? firstDate : obj.firstDate,
          lastDate: moment(lastDate).diff(moment(obj.lastDate)) > 0 ? lastDate : obj.lastDate,
        };
      }
    }, {});

  const monthsCount = moment(lastDate).diff(moment(firstDate), 'months');

  if (monthsCount < 3) {
    return {
      series: csatByDeviceAndDate.map((x, i) => {
        return {
          name: x.device,
          data: getData(x.csatByDate),
          color: getSeriesColor(i),
        };
      }),
      tooltipDateFormatter: ms => moment.utc(ms).format('dddd, D MMM Y'),
      xAxisDateFormatter: ms => moment.utc(ms).format('D MMM<br/>Y'),
    };
  } else if (monthsCount < 12) {
    return {
      series: csatByDeviceAndDate.map((x, i) => {
        return {
          name: x.device,
          data: getData(x.csatByDate, 'isoWeek'),
          color: getSeriesColor(i),
        };
      }),
      tooltipDateFormatter: ms => {
        return intl.formatMessage({ id: 'week' }) + moment.utc(ms).format(' W, Y');
      },
      xAxisDateFormatter: ms => {
        return intl.formatMessage({ id: 'week' }) + moment.utc(ms).format(' W<br/>Y');
      },
    };
  } else {
    return {
      series: csatByDeviceAndDate.map((x, i) => {
        return {
          name: x.device,
          data: getData(x.csatByDate, 'month'),
          color: getSeriesColor(i),
        };
      }),
      tooltipDateFormatter: ms => moment.utc(ms).format('MMMM Y'),
      xAxisDateFormatter: ms => moment.utc(ms).format('MMM<br/>Y'),
    };
  }
}

const styles = {
  wrapper: {
    padding: 15,
    paddingBottom: 0,
  },
};

class CsatByDeviceAndDateLineChart extends Component {
  render() {
    const intl = this.props.intl;
    const csatTitle = intl.formatMessage({
      id: 'csat',
      defaultMessage: 'CSAT',
    });

    const {
      series,
      tooltipDateFormatter,
      xAxisDateFormatter,
    } = getSeries(this.props.answersReport.csatByDeviceAndDate, intl);

    const chartConfig = {
      chart: {
        height: this.props.height - (styles.wrapper.padding),
        width: this.props.width - (styles.wrapper.padding * 2),
      },
      title: null,
      tooltip: {
        formatter: function () {
          const date = tooltipDateFormatter(this.x);
          const pointStrings = this.points.map(point => {
            const countMessage = intl.formatMessage({ id: 'numberOfAnswers' }, { count: point.point.count });
            const csat = ReactHighcharts.Highcharts.numberFormat(point.y, 1);
            return `
              <b style="color:${point.series.color}">${point.series.name}:</b>
              ${csatTitle} ${Math.round(Number(csat)).toFixed(0)}% (${countMessage})
            `;
          });
          return [date].concat(pointStrings);
        },
        split: true,
      },
      xAxis: {
        type: 'datetime',
        labels: {
          formatter: function () {
            return xAxisDateFormatter(this.value);
          },
          style: {
            color: grey500,
          },
        },
      },
      yAxis: {
        title: {
          text: csatTitle,
          style: {
            color: grey500,
          },
        },
        min: 0,
        max: 100,
        tickInterval: 1,
      },
      series,
    };

    return (
      <div style={styles.wrapper}>
        <ReactHighcharts
          config={chartConfig}
          domProps={{ id: this.props.uniqueId }}
        />
      </div>
    );
  }
}

export default createFragmentContainer(
  injectIntl(CsatByDeviceAndDateLineChart),
  graphql`
    fragment CsatByDeviceAndDateLineChart_answersReport on AnswersReport {
      csatByDeviceAndDate {
        device
        csatByDate {
          date
          csat
          total
        }
      }
    }
  `,
);



// WEBPACK FOOTER //
// ./src/components/CsatByDeviceAndDateLineChart/CsatByDeviceAndDateLineChart.js